import { type ReactNode, forwardRef, useEffect, useRef, useState } from 'react';
import { X } from '@phosphor-icons/react';

import TextFadeOverflowRolling from '@tmh-website/components/shared/TextFadeOverflowRolling';
import { Color } from '@tmh-website/shared/colors';
import { mergeRefs } from '@tmh-website/services/utils.service';

import * as Styled from './Tag.styles';
import { type TagThemeColor, tagThemes } from './Tag.theme';

export interface TagProps {
  themeColor?: TagThemeColor;
  label: ReactNode | string;
  size?: 'xSmall' | 'small' | 'large';
  href?: string;
  transparentText?: boolean;
  /**
   * Set an onClick event. This overwrites the href prop.
   */
  onClick?: () => void;
  onClickOnX?: () => void;
  ref?: any;
  /**
   * Set a max width in px. If the label is longer than the max width, it will be scrolling.
   */
  maxWidth?: number;
}

const Tag = forwardRef<HTMLDivElement, TagProps>(
  (
    {
      themeColor = Color.Neutral_200_OffWhite,
      label,
      size = 'small',
      onClickOnX,
      transparentText = false,
      href,
      onClick,
      maxWidth,
      ...props
    },
    ref
  ) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [rollingTextWidth, setRollingTextWidth] = useState<number | false>(false);
    const [initialLoad, setInitialLoad] = useState(true);

    useEffect(() => {
      const current = containerRef.current;
      if (current) {
        setInitialLoad(false);
      }
      if (!maxWidth || !current) {
        return;
      }
      requestAnimationFrame(() => {
        if (current.scrollWidth > maxWidth) {
          setRollingTextWidth(current.scrollWidth);
        }
      });
    }, [containerRef]);

    const labelEl = initialLoad ? (
      label
    ) : rollingTextWidth ? (
      <TextFadeOverflowRolling
        // 24px per second
        duration={(rollingTextWidth / 24) * 1000}
        fadeColor={tagThemes[themeColor].background}>
        {label}
      </TextFadeOverflowRolling>
    ) : (
      <Styled.Label>{label}</Styled.Label>
    );

    return (
      <Styled.Container
        as={href ? 'a' : onClick ? 'button' : 'span'}
        $themeColor={themeColor}
        $size={size}
        $hasRollingContainer={!!rollingTextWidth}
        $isLink={!!href || !!onClick}
        $hasTransparentText={transparentText}
        href={href}
        onClick={onClick}
        {...props}
        style={{ maxWidth: maxWidth ? `${maxWidth}px` : undefined }}
        ref={mergeRefs(ref, containerRef)}>
        {!!onClickOnX ? (
          <>
            {labelEl}
            <Styled.DeleteButton onClick={onClickOnX} type='button'>
              <X size={15} weight={'bold'} />
            </Styled.DeleteButton>
          </>
        ) : (
          labelEl
        )}
      </Styled.Container>
    );
  }
);

Tag.displayName = 'Tag';

export default Tag;
